import { useState, useEffect, useMemo } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { leadsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { 
  Loader2, 
  Plus,
  Search,
  Phone,
  Mail,
  Globe,
  MapPin,
  Building2,
  Calendar,
  PhoneCall,
  PhoneMissed,
  Voicemail,
  Target,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Users,
  LayoutGrid,
  Table as TableIcon,
  Filter,
  X,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  Pencil,
  Save,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const LEAD_STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800' },
  { value: 'called', label: 'Called', color: 'bg-amber-100 text-amber-800' },
  { value: 'contacted', label: 'Contacted', color: 'bg-purple-100 text-purple-800' },
  { value: 'interested', label: 'Interested', color: 'bg-orange-100 text-orange-800' },
  { value: 'demo_scheduled', label: 'Demo Scheduled', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'closed_won', label: 'Closed Won', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'closed_lost', label: 'Closed Lost', color: 'bg-red-100 text-red-800' },
];

const CALL_OUTCOMES = [
  { value: 'reached', label: 'Reached', icon: PhoneCall },
  { value: 'voicemail', label: 'Voicemail', icon: Voicemail },
  { value: 'no_answer', label: 'No Answer', icon: PhoneMissed },
  { value: 'callback_requested', label: 'Callback Requested', icon: Phone },
];

const LEAD_SOURCES = [
  { value: 'cold_call', label: 'Cold Call' },
  { value: 'referral', label: 'Referral' },
  { value: 'website', label: 'Website' },
  { value: 'trade_show', label: 'Trade Show' },
];

const FLEET_SIZES = [
  { value: '1-25', label: '1-25 units' },
  { value: '26-50', label: '26-50 units' },
  { value: '51-100', label: '51-100 units' },
  { value: '100+', label: '100+ units' },
];

const getStatusBadge = (status) => {
  const statusConfig = LEAD_STATUSES.find(s => s.value === status);
  return statusConfig ? statusConfig.color : 'bg-slate-100 text-slate-800';
};

const getStatusLabel = (status) => {
  const statusConfig = LEAD_STATUSES.find(s => s.value === status);
  return statusConfig ? statusConfig.label : status;
};

const isOverdue = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(dateStr);
  return targetDate < today;
};

const isToday = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date().toISOString().split('T')[0];
  return dateStr === today;
};

const getDaysUntilFollowup = (dateStr) => {
  if (!dateStr) return Infinity;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(dateStr);
  targetDate.setHours(0, 0, 0, 0);
  return Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));
};

export default function AdminLeads() {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCallLogDialog, setShowCallLogDialog] = useState(false);
  const [showFollowupDialog, setShowFollowupDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    source: 'all',
    fleetSize: 'all',
    state: 'all',
    hasFollowup: 'all', // 'all', 'overdue', 'today', 'upcoming', 'none'
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Sort state
  const [sortBy, setSortBy] = useState('followup'); // 'followup', 'company', 'status', 'created'
  const [sortDir, setSortDir] = useState('asc');
  
  // Form states
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    website: '',
    service_area: '',
    fleet_size: '',
    status: 'new',
    source: 'cold_call',
    notes: '',
    next_action_date: '',
    next_action: '',
  });
  
  const [callLogData, setCallLogData] = useState({
    call_date: new Date().toISOString().split('T')[0],
    call_time: '',
    duration_minutes: '',
    outcome: 'reached',
    notes: '',
    clear_followup: true, // New: option to clear followup
  });
  
  const [followupData, setFollowupData] = useState({
    next_action_date: '',
    next_action: '',
  });

  useEffect(() => {
    fetchLeads();
    fetchStats();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await leadsAPI.getAll();
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await leadsAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Check for duplicate company name
  const checkDuplicate = (companyName) => {
    if (!companyName.trim()) {
      setDuplicateWarning(null);
      return;
    }
    const normalized = companyName.trim().toLowerCase();
    const existing = leads.find(l => 
      l.company_name?.toLowerCase() === normalized ||
      l.company_name?.toLowerCase().includes(normalized) ||
      normalized.includes(l.company_name?.toLowerCase())
    );
    if (existing) {
      setDuplicateWarning(existing);
    } else {
      setDuplicateWarning(null);
    }
  };

  const handleCompanyNameChange = (value) => {
    setFormData({ ...formData, company_name: value });
    checkDuplicate(value);
  };

  const handleAddLead = async () => {
    if (!formData.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    
    setSaving(true);
    try {
      await leadsAPI.create(formData);
      toast.success('Lead added successfully');
      setShowAddDialog(false);
      resetForm();
      setDuplicateWarning(null);
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error('Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLead = async (leadId, updates) => {
    try {
      await leadsAPI.update(leadId, updates);
      toast.success('Lead updated');
      fetchLeads();
      fetchStats();
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => ({ ...prev, ...updates }));
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Failed to update lead');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      await leadsAPI.delete(leadId);
      toast.success('Lead deleted');
      setSelectedLead(null);
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Failed to delete lead');
    }
  };

  const handleAddCallLog = async () => {
    if (!selectedLead) return;
    
    setSaving(true);
    try {
      await leadsAPI.addCallLog(selectedLead.id, {
        ...callLogData,
        lead_id: selectedLead.id,
        duration_minutes: callLogData.duration_minutes ? parseInt(callLogData.duration_minutes) : null,
      });
      
      // If clear_followup is checked and followup was today or overdue, clear it
      const shouldClearFollowup = callLogData.clear_followup && 
        (isToday(selectedLead.next_action_date) || isOverdue(selectedLead.next_action_date));
      
      if (shouldClearFollowup) {
        await leadsAPI.update(selectedLead.id, {
          next_action_date: null,
          next_action: null,
        });
        toast.success('Call logged and follow-up cleared');
      } else {
        toast.success('Call log added');
      }
      
      setShowCallLogDialog(false);
      resetCallLogForm();
      
      // Refresh the lead to show updated call logs
      const response = await leadsAPI.getById(selectedLead.id);
      setSelectedLead(response.data);
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error adding call log:', error);
      toast.error('Failed to add call log');
    } finally {
      setSaving(false);
    }
  };

  const handleSetFollowup = async () => {
    if (!selectedLead) return;
    
    setSaving(true);
    try {
      await leadsAPI.update(selectedLead.id, {
        next_action_date: followupData.next_action_date || null,
        next_action: followupData.next_action || null,
      });
      toast.success('Follow-up updated');
      setShowFollowupDialog(false);
      
      const response = await leadsAPI.getById(selectedLead.id);
      setSelectedLead(response.data);
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error updating follow-up:', error);
      toast.error('Failed to update follow-up');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      contact_name: '',
      email: '',
      phone: '',
      website: '',
      service_area: '',
      fleet_size: '',
      status: 'new',
      source: 'cold_call',
      notes: '',
      next_action_date: '',
      next_action: '',
    });
  };

  const resetCallLogForm = () => {
    setCallLogData({
      call_date: new Date().toISOString().split('T')[0],
      call_time: '',
      duration_minutes: '',
      outcome: 'reached',
      notes: '',
      clear_followup: true,
    });
  };

  const openLeadDetail = async (lead) => {
    try {
      const response = await leadsAPI.getById(lead.id);
      setSelectedLead(response.data);
    } catch (error) {
      console.error('Error fetching lead details:', error);
      setSelectedLead(lead);
    }
  };

  const openFollowupDialog = () => {
    setFollowupData({
      next_action_date: selectedLead?.next_action_date || '',
      next_action: selectedLead?.next_action || '',
    });
    setShowFollowupDialog(true);
  };

  const enterEditMode = () => {
    setEditFormData({
      company_name: selectedLead?.company_name || '',
      contact_name: selectedLead?.contact_name || '',
      email: selectedLead?.email || '',
      phone: selectedLead?.phone || '',
      website: selectedLead?.website || '',
      service_area: selectedLead?.service_area || '',
      fleet_size: selectedLead?.fleet_size || '',
      source: selectedLead?.source || 'cold_call',
      notes: selectedLead?.notes || '',
    });
    setIsEditMode(true);
  };

  const cancelEditMode = () => {
    setIsEditMode(false);
    setEditFormData(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedLead || !editFormData) return;
    
    if (!editFormData.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    
    setSaving(true);
    try {
      await leadsAPI.update(selectedLead.id, editFormData);
      toast.success('Lead updated successfully');
      
      // Refresh the lead data
      const response = await leadsAPI.getById(selectedLead.id);
      setSelectedLead(response.data);
      fetchLeads();
      fetchStats();
      setIsEditMode(false);
      setEditFormData(null);
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  // Filter and sort leads
  const filteredAndSortedLeads = useMemo(() => {
    let result = [...leads];
    
    // Apply filters
    if (filters.search) {
      const query = filters.search.toLowerCase();
      result = result.filter(lead =>
        lead.company_name?.toLowerCase().includes(query) ||
        lead.contact_name?.toLowerCase().includes(query) ||
        lead.service_area?.toLowerCase().includes(query) ||
        lead.phone?.includes(query)
      );
    }
    
    if (filters.status !== 'all') {
      result = result.filter(lead => lead.status === filters.status);
    }
    
    if (filters.source !== 'all') {
      result = result.filter(lead => lead.source === filters.source);
    }
    
    if (filters.fleetSize !== 'all') {
      result = result.filter(lead => lead.fleet_size === filters.fleetSize);
    }
    
    if (filters.state !== 'all') {
      result = result.filter(lead => {
        if (!lead.service_area) return false;
        const parts = lead.service_area.split(',');
        if (parts.length >= 2) {
          const statePart = parts[parts.length - 1].trim().toUpperCase();
          return statePart === filters.state;
        }
        return false;
      });
    }
    
    if (filters.hasFollowup !== 'all') {
      const today = new Date().toISOString().split('T')[0];
      switch (filters.hasFollowup) {
        case 'overdue':
          result = result.filter(lead => lead.next_action_date && lead.next_action_date < today);
          break;
        case 'today':
          result = result.filter(lead => lead.next_action_date === today);
          break;
        case 'needs_attention':
          // Both overdue AND due today
          result = result.filter(lead => lead.next_action_date && lead.next_action_date <= today);
          break;
        case 'upcoming':
          result = result.filter(lead => lead.next_action_date && lead.next_action_date > today);
          break;
        case 'none':
          result = result.filter(lead => !lead.next_action_date);
          break;
      }
    }
    
    // Sort - warmest leads first (soonest followup, then overdue)
    result.sort((a, b) => {
      if (sortBy === 'followup') {
        const daysA = getDaysUntilFollowup(a.next_action_date);
        const daysB = getDaysUntilFollowup(b.next_action_date);
        // Overdue and today first, then by date
        if (daysA <= 0 && daysB > 0) return -1;
        if (daysB <= 0 && daysA > 0) return 1;
        return sortDir === 'asc' ? daysA - daysB : daysB - daysA;
      } else if (sortBy === 'company') {
        return sortDir === 'asc' 
          ? (a.company_name || '').localeCompare(b.company_name || '')
          : (b.company_name || '').localeCompare(a.company_name || '');
      } else if (sortBy === 'status') {
        const statusOrder = LEAD_STATUSES.map(s => s.value);
        const indexA = statusOrder.indexOf(a.status);
        const indexB = statusOrder.indexOf(b.status);
        return sortDir === 'asc' ? indexA - indexB : indexB - indexA;
      } else if (sortBy === 'created') {
        return sortDir === 'asc'
          ? new Date(a.created_at) - new Date(b.created_at)
          : new Date(b.created_at) - new Date(a.created_at);
      }
      return 0;
    });
    
    return result;
  }, [leads, filters, sortBy, sortDir]);

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => 
    key !== 'search' && value !== 'all'
  ).length;

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      source: 'all',
      fleetSize: 'all',
      state: 'all',
      hasFollowup: 'all',
    });
  };

  const statusCounts = {
    new: stats?.by_status?.new || 0,
    called: stats?.by_status?.called || 0,
    interested: stats?.by_status?.interested || 0,
    closed_won: stats?.by_status?.closed_won || 0,
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  // Extract unique states from leads' service_area field
  const uniqueStates = useMemo(() => {
    const stateSet = new Set();
    leads.forEach(lead => {
      if (lead.service_area) {
        // Try to extract state from formats like "City, ST" or "City, State"
        const parts = lead.service_area.split(',');
        if (parts.length >= 2) {
          const statePart = parts[parts.length - 1].trim().toUpperCase();
          // Check if it's a 2-letter state code or full state name
          if (statePart.length === 2) {
            stateSet.add(statePart);
          } else if (statePart.length > 2) {
            // Could be full state name, add it
            stateSet.add(statePart);
          }
        }
      }
    });
    return Array.from(stateSet).sort();
  }, [leads]);

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  return (
    <AdminLayout title="Sales Leads">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card 
          className={`border-slate-200 cursor-pointer hover:border-blue-300 transition-colors ${filters.status === 'new' ? 'border-blue-500 bg-blue-50' : ''}`}
          onClick={() => setFilters({...filters, status: filters.status === 'new' ? 'all' : 'new'})}
        >
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{statusCounts.new}</p>
            <p className="text-sm text-slate-500">New</p>
          </CardContent>
        </Card>
        <Card 
          className={`border-slate-200 cursor-pointer hover:border-amber-300 transition-colors ${filters.status === 'called' ? 'border-amber-500 bg-amber-50' : ''}`}
          onClick={() => setFilters({...filters, status: filters.status === 'called' ? 'all' : 'called'})}
        >
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{statusCounts.called}</p>
            <p className="text-sm text-slate-500">Called</p>
          </CardContent>
        </Card>
        <Card 
          className={`border-slate-200 cursor-pointer hover:border-orange-300 transition-colors ${filters.status === 'interested' ? 'border-orange-500 bg-orange-50' : ''}`}
          onClick={() => setFilters({...filters, status: filters.status === 'interested' ? 'all' : 'interested'})}
        >
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{statusCounts.interested}</p>
            <p className="text-sm text-slate-500">Interested</p>
          </CardContent>
        </Card>
        <Card 
          className={`border-slate-200 cursor-pointer hover:border-emerald-300 transition-colors ${filters.status === 'closed_won' ? 'border-emerald-500 bg-emerald-50' : ''}`}
          onClick={() => setFilters({...filters, status: filters.status === 'closed_won' ? 'all' : 'closed_won'})}
        >
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{statusCounts.closed_won}</p>
            <p className="text-sm text-slate-500">Won</p>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <Input
              placeholder="Search by company, contact, area, or phone..."
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className={activeFilterCount > 0 ? 'border-[#52B8E1] text-[#194461]' : ''}
            >
              <Filter size={18} className="mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className="ml-2 bg-[#52B8E1] text-white">{activeFilterCount}</Badge>
              )}
            </Button>
            <div className="flex border rounded-lg overflow-hidden">
              <Button 
                variant={viewMode === 'cards' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('cards')}
                className={viewMode === 'cards' ? 'bg-[#194461]' : ''}
              >
                <LayoutGrid size={18} />
              </Button>
              <Button 
                variant={viewMode === 'table' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('table')}
                className={viewMode === 'table' ? 'bg-[#194461]' : ''}
              >
                <TableIcon size={18} />
              </Button>
            </div>
            <Button onClick={() => setShowAddDialog(true)} className="bg-[#194461] hover:bg-[#0d2d42]">
              <Plus size={18} className="mr-2" />
              Add Lead
            </Button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-slate-50 border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-sm text-slate-700">Filters</span>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500">
                  <X size={14} className="mr-1" /> Clear all
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {LEAD_STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">State</Label>
                <Select value={filters.state} onValueChange={(v) => setFilters({...filters, state: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All States</SelectItem>
                    {uniqueStates.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Source</Label>
                <Select value={filters.source} onValueChange={(v) => setFilters({...filters, source: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    {LEAD_SOURCES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Fleet Size</Label>
                <Select value={filters.fleetSize} onValueChange={(v) => setFilters({...filters, fleetSize: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sizes</SelectItem>
                    {FLEET_SIZES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Follow-up</Label>
                <Select value={filters.hasFollowup} onValueChange={(v) => setFilters({...filters, hasFollowup: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="needs_attention">Needs Attention (Today + Overdue)</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="today">Due Today</SelectItem>
                    <SelectItem value="upcoming">Upcoming</SelectItem>
                    <SelectItem value="none">No Follow-up Set</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Needs Follow-up Alert */}
      {stats?.needs_followup > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="text-amber-600" size={20} />
          <span className="text-amber-800">
            <strong>{stats.needs_followup}</strong> lead{stats.needs_followup > 1 ? 's' : ''} need follow-up today or are overdue
          </span>
          <Button 
            variant="outline" 
            size="sm" 
            className="ml-auto border-amber-300 text-amber-700"
            onClick={() => setFilters({...filters, hasFollowup: 'needs_attention'})}
          >
            Show all
          </Button>
        </div>
      )}

      {/* Lead List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-[#194461]" size={32} />
        </div>
      ) : filteredAndSortedLeads.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Target size={48} className="mx-auto mb-4 opacity-30" />
          <p>No leads found</p>
          {activeFilterCount > 0 && (
            <Button variant="outline" className="mt-4" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
          {activeFilterCount === 0 && (
            <Button variant="outline" className="mt-4" onClick={() => setShowAddDialog(true)}>
              Add your first lead
            </Button>
          )}
        </div>
      ) : viewMode === 'cards' ? (
        /* Card View */
        <div className="grid gap-4">
          {filteredAndSortedLeads.map((lead) => (
            <Card 
              key={lead.id} 
              className={`border-slate-200 hover:border-[#52B8E1] cursor-pointer transition-colors ${
                isOverdue(lead.next_action_date) ? 'border-l-4 border-l-red-500' :
                isToday(lead.next_action_date) ? 'border-l-4 border-l-amber-500' : ''
              }`}
              onClick={() => openLeadDetail(lead)}
            >
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-900">{lead.company_name}</h3>
                      <Badge className={getStatusBadge(lead.status)}>
                        {getStatusLabel(lead.status)}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm text-slate-600">
                      {lead.contact_name && (
                        <div className="flex items-center gap-1">
                          <Users size={14} />
                          <span>{lead.contact_name}</span>
                        </div>
                      )}
                      {lead.phone && (
                        <div className="flex items-center gap-1">
                          <Phone size={14} />
                          <span>{lead.phone}</span>
                        </div>
                      )}
                      {lead.service_area && (
                        <div className="flex items-center gap-1">
                          <MapPin size={14} />
                          <span>{lead.service_area}</span>
                        </div>
                      )}
                      {lead.fleet_size && (
                        <div className="flex items-center gap-1">
                          <Building2 size={14} />
                          <span>{lead.fleet_size}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {lead.next_action_date && (
                      <div className={`flex items-center gap-1 text-sm px-2 py-1 rounded ${
                        isOverdue(lead.next_action_date) ? 'bg-red-100 text-red-700 font-medium' : 
                        isToday(lead.next_action_date) ? 'bg-amber-100 text-amber-700 font-medium' :
                        'text-slate-500'
                      }`}>
                        <Calendar size={14} />
                        <span>{lead.next_action_date}</span>
                        {isOverdue(lead.next_action_date) && <AlertCircle size={14} />}
                        {isToday(lead.next_action_date) && <span className="text-xs">(Today)</span>}
                      </div>
                    )}
                    {lead.next_action && (
                      <span className="text-xs text-slate-400">{lead.next_action}</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* Table View */
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead 
                  className="cursor-pointer hover:bg-slate-100"
                  onClick={() => toggleSort('company')}
                >
                  <div className="flex items-center gap-1">
                    Company <SortIcon field="company" />
                  </div>
                </TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Fleet</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-100"
                  onClick={() => toggleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status <SortIcon field="status" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-100"
                  onClick={() => toggleSort('followup')}
                >
                  <div className="flex items-center gap-1">
                    Follow-up <SortIcon field="followup" />
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedLeads.map((lead) => (
                <TableRow 
                  key={lead.id} 
                  className={`cursor-pointer hover:bg-slate-50 ${
                    isOverdue(lead.next_action_date) ? 'bg-red-50' :
                    isToday(lead.next_action_date) ? 'bg-amber-50' : ''
                  }`}
                  onClick={() => openLeadDetail(lead)}
                >
                  <TableCell className="font-medium">{lead.company_name}</TableCell>
                  <TableCell>{lead.contact_name || '-'}</TableCell>
                  <TableCell>{lead.phone || '-'}</TableCell>
                  <TableCell>{lead.service_area || '-'}</TableCell>
                  <TableCell>{lead.fleet_size || '-'}</TableCell>
                  <TableCell>
                    <Badge className={getStatusBadge(lead.status)}>
                      {getStatusLabel(lead.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {lead.next_action_date ? (
                      <div className={`flex items-center gap-1 text-sm ${
                        isOverdue(lead.next_action_date) ? 'text-red-600 font-medium' :
                        isToday(lead.next_action_date) ? 'text-amber-600 font-medium' : ''
                      }`}>
                        {lead.next_action_date}
                        {isOverdue(lead.next_action_date) && <AlertCircle size={14} />}
                      </div>
                    ) : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add Lead Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) {
          resetForm();
          setDuplicateWarning(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
            <DialogDescription>Enter the lead information for your sales pipeline.</DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {/* Duplicate Warning */}
            {duplicateWarning && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="text-amber-600 mt-0.5" size={18} />
                <div>
                  <p className="text-sm font-medium text-amber-800">Possible duplicate found</p>
                  <p className="text-sm text-amber-700">
                    "{duplicateWarning.company_name}" already exists in your leads.
                    <Button 
                      variant="link" 
                      className="text-amber-700 underline p-0 h-auto ml-1"
                      onClick={() => {
                        setShowAddDialog(false);
                        openLeadDetail(duplicateWarning);
                      }}
                    >
                      View existing lead
                    </Button>
                  </p>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => handleCompanyNameChange(e.target.value)}
                  placeholder="ABC Portable Toilets"
                  className={duplicateWarning ? 'border-amber-300' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_name">Contact Name</Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@abc-portables.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="www.abc-portables.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service_area">Service Area</Label>
                <Input
                  id="service_area"
                  value={formData.service_area}
                  onChange={(e) => setFormData({ ...formData, service_area: e.target.value })}
                  placeholder="Dallas, TX"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fleet_size">Fleet Size</Label>
                <Select value={formData.fleet_size} onValueChange={(v) => setFormData({ ...formData, fleet_size: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    {FLEET_SIZES.map(size => (
                      <SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="source">Lead Source</Label>
                <Select value={formData.source} onValueChange={(v) => setFormData({ ...formData, source: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_SOURCES.map(source => (
                      <SelectItem key={source.value} value={source.value}>{source.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUSES.map(status => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="next_action_date">Next Follow-up Date</Label>
                <Input
                  id="next_action_date"
                  type="date"
                  value={formData.next_action_date}
                  onChange={(e) => setFormData({ ...formData, next_action_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="next_action">Follow-up Action</Label>
                <Input
                  id="next_action"
                  value={formData.next_action}
                  onChange={(e) => setFormData({ ...formData, next_action: e.target.value })}
                  placeholder="Follow up call"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this lead..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddLead} disabled={saving} className="bg-[#194461] hover:bg-[#0d2d42]">
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Add Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={(open) => {
        if (!open) {
          setSelectedLead(null);
          setIsEditMode(false);
          setEditFormData(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedLead && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    {isEditMode ? (
                      <DialogTitle className="text-xl">Edit Lead</DialogTitle>
                    ) : (
                      <>
                        <DialogTitle className="text-xl">{selectedLead.company_name}</DialogTitle>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={getStatusBadge(selectedLead.status)}>
                            {getStatusLabel(selectedLead.status)}
                          </Badge>
                          {selectedLead.source && (
                            <span className="text-sm text-slate-500">via {selectedLead.source.replace('_', ' ')}</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {!isEditMode && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={enterEditMode}
                          className="text-[#194461]"
                        >
                          <Pencil size={16} className="mr-1" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeleteLead(selectedLead.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </DialogHeader>
              
              {isEditMode && editFormData ? (
                /* Edit Mode */
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit_company_name">Company Name *</Label>
                      <Input
                        id="edit_company_name"
                        value={editFormData.company_name}
                        onChange={(e) => setEditFormData({ ...editFormData, company_name: e.target.value })}
                        placeholder="ABC Portable Toilets"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_contact_name">Contact Name</Label>
                      <Input
                        id="edit_contact_name"
                        value={editFormData.contact_name}
                        onChange={(e) => setEditFormData({ ...editFormData, contact_name: e.target.value })}
                        placeholder="John Smith"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit_email">Email</Label>
                      <Input
                        id="edit_email"
                        type="email"
                        value={editFormData.email}
                        onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                        placeholder="john@abc-portables.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_phone">Phone</Label>
                      <Input
                        id="edit_phone"
                        value={editFormData.phone}
                        onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit_website">Website</Label>
                      <Input
                        id="edit_website"
                        value={editFormData.website}
                        onChange={(e) => setEditFormData({ ...editFormData, website: e.target.value })}
                        placeholder="www.abc-portables.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_service_area">Service Area</Label>
                      <Input
                        id="edit_service_area"
                        value={editFormData.service_area}
                        onChange={(e) => setEditFormData({ ...editFormData, service_area: e.target.value })}
                        placeholder="Dallas, TX"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit_fleet_size">Fleet Size</Label>
                      <Select value={editFormData.fleet_size || 'not_specified'} onValueChange={(v) => setEditFormData({ ...editFormData, fleet_size: v === 'not_specified' ? '' : v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not_specified">Not specified</SelectItem>
                          {FLEET_SIZES.map(size => (
                            <SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_source">Lead Source</Label>
                      <Select value={editFormData.source || 'cold_call'} onValueChange={(v) => setEditFormData({ ...editFormData, source: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                        <SelectContent>
                          {LEAD_SOURCES.map(source => (
                            <SelectItem key={source.value} value={source.value}>{source.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="edit_notes">Notes</Label>
                    <Textarea
                      id="edit_notes"
                      value={editFormData.notes}
                      onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                      placeholder="Additional notes about this lead..."
                      rows={3}
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button variant="outline" onClick={cancelEditMode} disabled={saving}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveEdit} disabled={saving} className="bg-[#194461] hover:bg-[#0d2d42]">
                      {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                      Save Changes
                    </Button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div className="grid gap-6 py-4">
                  {/* Contact Info */}
                  <div className="grid grid-cols-2 gap-4">
                    {selectedLead.contact_name && (
                      <div className="flex items-center gap-2 text-sm">
                        <Users size={16} className="text-slate-400" />
                        <span>{selectedLead.contact_name}</span>
                      </div>
                    )}
                    {selectedLead.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone size={16} className="text-slate-400" />
                        <a href={`tel:${selectedLead.phone}`} className="text-[#52B8E1] hover:underline">{selectedLead.phone}</a>
                      </div>
                    )}
                    {selectedLead.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail size={16} className="text-slate-400" />
                        <a href={`mailto:${selectedLead.email}`} className="text-[#52B8E1] hover:underline">{selectedLead.email}</a>
                      </div>
                    )}
                    {selectedLead.website && (
                      <div className="flex items-center gap-2 text-sm">
                        <Globe size={16} className="text-slate-400" />
                        <a href={selectedLead.website.startsWith('http') ? selectedLead.website : `https://${selectedLead.website}`} target="_blank" rel="noopener noreferrer" className="text-[#52B8E1] hover:underline">{selectedLead.website}</a>
                      </div>
                    )}
                    {selectedLead.service_area && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin size={16} className="text-slate-400" />
                        <span>{selectedLead.service_area}</span>
                      </div>
                    )}
                    {selectedLead.fleet_size && (
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 size={16} className="text-slate-400" />
                        <span>{selectedLead.fleet_size}</span>
                      </div>
                    )}
                  </div>

                  {/* Follow-up Section */}
                  <div className={`p-4 rounded-lg ${
                    isOverdue(selectedLead.next_action_date) ? 'bg-red-50 border border-red-200' : 
                    isToday(selectedLead.next_action_date) ? 'bg-amber-50 border border-amber-200' :
                    'bg-slate-50 border border-slate-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs text-slate-500 uppercase">Next Follow-up</Label>
                        {selectedLead.next_action_date ? (
                          <div className="mt-1">
                            <div className={`flex items-center gap-2 font-medium ${
                              isOverdue(selectedLead.next_action_date) ? 'text-red-700' :
                              isToday(selectedLead.next_action_date) ? 'text-amber-700' : ''
                            }`}>
                              <Calendar size={16} />
                              <span>{selectedLead.next_action_date}</span>
                              {isOverdue(selectedLead.next_action_date) && <Badge variant="destructive">Overdue</Badge>}
                              {isToday(selectedLead.next_action_date) && <Badge className="bg-amber-500">Today</Badge>}
                            </div>
                            {selectedLead.next_action && (
                              <p className="text-sm text-slate-600 mt-1">{selectedLead.next_action}</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-400 mt-1">No follow-up scheduled</p>
                        )}
                      </div>
                      <Button variant="outline" size="sm" onClick={openFollowupDialog}>
                        <Calendar size={14} className="mr-1" />
                        {selectedLead.next_action_date ? 'Change' : 'Set'} Follow-up
                      </Button>
                    </div>
                  </div>

                  {/* Notes */}
                  {selectedLead.notes && (
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <Label className="text-xs text-slate-500 uppercase">Notes</Label>
                      <p className="text-sm mt-1">{selectedLead.notes}</p>
                    </div>
                  )}

                  {/* Status Change */}
                  <div className="border-t pt-4">
                    <Label className="text-sm text-slate-500 mb-2 block">Change Status</Label>
                    <div className="flex flex-wrap gap-2">
                      {LEAD_STATUSES.map(status => (
                        <Button
                          key={status.value}
                          variant={selectedLead.status === status.value ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleUpdateLead(selectedLead.id, { status: status.value })}
                          className={selectedLead.status === status.value ? 'bg-[#194461]' : ''}
                        >
                          {status.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Call Logs */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <Label className="text-sm text-slate-500 uppercase">Call History</Label>
                      <Button size="sm" variant="outline" onClick={() => setShowCallLogDialog(true)}>
                        <PhoneCall size={14} className="mr-1" />
                        Log Call
                      </Button>
                    </div>
                    
                    {selectedLead.call_logs && selectedLead.call_logs.length > 0 ? (
                      <div className="space-y-3">
                        {[...selectedLead.call_logs].reverse().map((log, index) => (
                          <div key={log.id || index} className="bg-slate-50 p-3 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                {log.outcome === 'reached' && <CheckCircle2 size={16} className="text-emerald-600" />}
                                {log.outcome === 'voicemail' && <Voicemail size={16} className="text-amber-600" />}
                                {log.outcome === 'no_answer' && <PhoneMissed size={16} className="text-red-500" />}
                                {log.outcome === 'callback_requested' && <Phone size={16} className="text-blue-600" />}
                                <span className="font-medium text-sm capitalize">{log.outcome.replace('_', ' ')}</span>
                              </div>
                              <span className="text-xs text-slate-400">
                                {log.call_date} {log.call_time && `at ${log.call_time}`}
                                {log.duration_minutes && ` (${log.duration_minutes} min)`}
                              </span>
                            </div>
                            {log.notes && <p className="text-sm text-slate-600">{log.notes}</p>}
                            {log.caller_name && <p className="text-xs text-slate-400 mt-1">by {log.caller_name}</p>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-4">No calls logged yet</p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Call Log Dialog */}
      <Dialog open={showCallLogDialog} onOpenChange={setShowCallLogDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Call</DialogTitle>
            <DialogDescription>Record a call with {selectedLead?.company_name}</DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="call_date">Call Date</Label>
                <Input
                  id="call_date"
                  type="date"
                  value={callLogData.call_date}
                  onChange={(e) => setCallLogData({ ...callLogData, call_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="call_time">Time</Label>
                <Input
                  id="call_time"
                  type="time"
                  value={callLogData.call_time}
                  onChange={(e) => setCallLogData({ ...callLogData, call_time: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="outcome">Outcome</Label>
                <Select value={callLogData.outcome} onValueChange={(v) => setCallLogData({ ...callLogData, outcome: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select outcome" />
                  </SelectTrigger>
                  <SelectContent>
                    {CALL_OUTCOMES.map(outcome => (
                      <SelectItem key={outcome.value} value={outcome.value}>{outcome.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  value={callLogData.duration_minutes}
                  onChange={(e) => setCallLogData({ ...callLogData, duration_minutes: e.target.value })}
                  placeholder="5"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="call_notes">Notes</Label>
              <Textarea
                id="call_notes"
                value={callLogData.notes}
                onChange={(e) => setCallLogData({ ...callLogData, notes: e.target.value })}
                placeholder="What was discussed..."
                rows={3}
              />
            </div>

            {/* Clear follow-up option */}
            {(isToday(selectedLead?.next_action_date) || isOverdue(selectedLead?.next_action_date)) && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
                <input
                  type="checkbox"
                  id="clear_followup"
                  checked={callLogData.clear_followup}
                  onChange={(e) => setCallLogData({ ...callLogData, clear_followup: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="clear_followup" className="text-sm text-amber-800 cursor-pointer">
                  Clear follow-up (was due {isToday(selectedLead?.next_action_date) ? 'today' : 'earlier'})
                </Label>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCallLogDialog(false)}>Cancel</Button>
            <Button onClick={handleAddCallLog} disabled={saving} className="bg-[#194461] hover:bg-[#0d2d42]">
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Save Call Log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Follow-up Dialog */}
      <Dialog open={showFollowupDialog} onOpenChange={setShowFollowupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Follow-up</DialogTitle>
            <DialogDescription>Schedule when to follow up with {selectedLead?.company_name}</DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="followup_date">Follow-up Date</Label>
              <Input
                id="followup_date"
                type="date"
                value={followupData.next_action_date}
                onChange={(e) => setFollowupData({ ...followupData, next_action_date: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="followup_action">Action / Reminder</Label>
              <Input
                id="followup_action"
                value={followupData.next_action}
                onChange={(e) => setFollowupData({ ...followupData, next_action: e.target.value })}
                placeholder="e.g., Follow up call, Send proposal, Schedule demo"
              />
            </div>

            {/* Quick date buttons */}
            <div className="flex flex-wrap gap-2">
              <Label className="w-full text-xs text-slate-500">Quick set:</Label>
              {[
                { label: 'Tomorrow', days: 1 },
                { label: 'In 3 days', days: 3 },
                { label: 'In 1 week', days: 7 },
                { label: 'In 2 weeks', days: 14 },
                { label: 'In 1 month', days: 30 },
              ].map(({ label, days }) => {
                const date = new Date();
                date.setDate(date.getDate() + days);
                const dateStr = date.toISOString().split('T')[0];
                return (
                  <Button
                    key={days}
                    variant="outline"
                    size="sm"
                    onClick={() => setFollowupData({ ...followupData, next_action_date: dateStr })}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setFollowupData({ next_action_date: '', next_action: '' });
                handleSetFollowup();
              }}
              className="text-red-600"
            >
              Clear Follow-up
            </Button>
            <Button variant="outline" onClick={() => setShowFollowupDialog(false)}>Cancel</Button>
            <Button onClick={handleSetFollowup} disabled={saving} className="bg-[#194461] hover:bg-[#0d2d42]">
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
